import React from "react";
import { Skeleton } from "antd";

import {
  HBar,
  MetricCard,
  SectionCard,
  barColor,
  cellBg,
  fmtNum,
  fmtPct,
  useMasteryApi,
} from "./shared";

// Ported from the standalone Golden Explorer's /api/health (see
// views_portfolio_mastery_health.py). Aggregates only — the record-level
// enrichment backlog lives in the queue's Data Gaps tab.

interface Scorecard {
  n_total: number;
  n_golden: number;
  n_provisional: number;
  n_quarantine: number;
  n_low_conf: number;
  pct_golden: number | null;
  n_corroborated: number;
  pct_corroborated: number | null;
  n_normalized: number;
  n_merged: number;
  ratio: number | null;
  pct_phone: number | null;
  pct_email: number | null;
  pct_pi: number | null;
}

export interface MasteryHealthPayload {
  scorecard: Scorecard;
  /** Why the admission gate held records back, most common first. */
  demotions: Array<{ reason: string; n: number }>;
  corroboration: Array<{ n_src: number; n: number }>;
  fan_in: Array<{ bucket: string; n: number }>;
  coverage: Array<{ field: string; pct: number | null; note?: string }>;
  record_flags: { nameless: number; geo_conflict: number; relocated: number };
  fill_by_source: {
    fields: string[];
    rows: Array<Record<string, string | number>>;
  };
}

export default function DataMasteryHealth() {
  const { data, isLoading } =
    useMasteryApi<MasteryHealthPayload>("mastery-health/");

  if (isLoading || !data) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  const s = data.scorecard;
  const flags = data.record_flags;
  const demotions = data.demotions ?? [];
  const demotionMax = Math.max(...demotions.map((d) => d.n), 1);
  const corrMax = Math.max(...data.corroboration.map((d) => d.n), 1);
  const fanMax = Math.max(...data.fan_in.map((d) => d.n), 1);
  const geocoded = data.coverage.find((c) => c.field === "geocoded");

  return (
    <div>
      {/* Trust scorecard — each card carries its breakdown as real metrics */}
      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* Quarantine is shown as its own row: a feed that fails the admission
            gate wholesale reads as "0% golden" with no accounting for the
            records unless every tier is on the card. */}
        <MetricCard
          label="Golden tier"
          value={fmtPct(s.pct_golden)}
          rows={[
            { label: "Golden", value: fmtNum(s.n_golden) },
            { label: "Provisional", value: fmtNum(s.n_provisional) },
            { label: "Quarantined", value: fmtNum(s.n_quarantine) },
            { label: "Low-confidence", value: fmtNum(s.n_low_conf) },
          ]}
        />
        <MetricCard
          label="Cross-source corroborated"
          value={fmtPct(s.pct_corroborated)}
          rows={[
            { label: "2+ sources", value: fmtNum(s.n_corroborated) },
            {
              label: "Single-source",
              value: fmtNum(s.n_total - s.n_corroborated),
            },
          ]}
        />
        <MetricCard
          label="Consolidation"
          value={s.ratio == null ? "—" : `${s.ratio} : 1`}
          rows={[
            { label: "Normalized records", value: fmtNum(s.n_normalized) },
            { label: "Golden sites", value: fmtNum(s.n_total) },
            { label: "Merged (2+ records)", value: fmtNum(s.n_merged) },
          ]}
        />
        <MetricCard
          label="Contact reachability"
          value={fmtPct(s.pct_phone)}
          rows={[
            { label: "Phone", value: fmtPct(s.pct_phone) },
            { label: "Email", value: fmtPct(s.pct_email) },
            { label: "PI", value: fmtPct(s.pct_pi) },
          ]}
        />
      </div>

      {/* Why records did not reach the golden tier. Only rendered when the
          admission gate actually demoted something, so a clean tenant keeps
          the surface it had. */}
      {demotions.length > 0 && (
        <SectionCard
          title="Admission gate — why records were held back"
          meta="demotion reason recorded by the mastering run, most common first"
        >
          {demotions.map((d) => (
            <HBar
              key={d.reason}
              label={d.reason.replace(/_/g, " ").toLowerCase()}
              n={d.n}
              max={demotionMax}
              color="#D97706"
            />
          ))}
        </SectionCard>
      )}

      {/* Corroboration + merge fan-in */}
      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Source corroboration"
          meta="distinct vendor feeds per golden site · teal = 2+"
        >
          {data.corroboration.map((d) => (
            <HBar
              key={d.n_src}
              label={`${d.n_src} src`}
              n={d.n}
              max={corrMax}
              color={d.n_src >= 2 ? "#0E7490" : "#94a3b8"}
            />
          ))}
        </SectionCard>
        <SectionCard
          title="Merge fan-in"
          meta="raw records collapsed into each golden site"
        >
          {data.fan_in.map((d) => (
            <HBar
              key={d.bucket}
              label={d.bucket}
              n={d.n}
              max={fanMax}
              color="#94a3b8"
            />
          ))}
        </SectionCard>
      </div>

      {/* Attribute coverage */}
      <SectionCard
        title="Attribute coverage"
        meta="% of golden records carrying each canonical field"
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-x-7 gap-y-1">
          {data.coverage.map((c) => (
            <div
              key={c.field}
              className="my-1 grid grid-cols-[110px_1fr_70px] items-center gap-2.5"
            >
              <span className="text-right text-xs text-[#556C75]">
                {c.field}
              </span>
              <div className="h-3.5 rounded bg-[#f1f5f9]">
                <div
                  className="h-full rounded"
                  style={{
                    width: `${c.pct ?? 0}%`,
                    background: barColor(c.pct ?? 0),
                  }}
                />
              </div>
              <span className="text-right text-xs tabular-nums text-[#2B4752]">
                {fmtPct(c.pct)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-6 border-t border-[#E6EEF2] pt-2">
          {[
            { label: "Building-level geocode", value: geocoded?.note ?? "—" },
            { label: "Nameless goldens", value: fmtNum(flags.nameless) },
            { label: "Geo conflicts", value: fmtNum(flags.geo_conflict) },
            { label: "Relocated", value: fmtNum(flags.relocated) },
          ].map((f) => (
            <span key={f.label} className="text-sm text-[#2B4752]">
              {f.label}:{" "}
              <span className="font-semibold tabular-nums">{f.value}</span>
            </span>
          ))}
        </div>
      </SectionCard>

      {/* Fill-rate by source */}
      <SectionCard title="Field fill-rate by source (what each vendor actually delivers)">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="text-[#556C75]">
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-right">rows</th>
              {data.fill_by_source.fields.map((f) => (
                <th key={f} className="p-2 text-center">
                  {f.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.fill_by_source.rows.map((r) => (
              <tr key={String(r.source)} className="border-t border-[#f0f0f0]">
                <td className="p-2 font-medium text-[#2B4752]">{r.source}</td>
                <td className="p-2 text-right tabular-nums">
                  {fmtNum(r.n as number)}
                </td>
                {data.fill_by_source.fields.map((f) => (
                  <td
                    key={f}
                    className="p-2 text-center tabular-nums"
                    style={{ background: cellBg((r[f] as number) ?? 0) }}
                  >
                    {r[f] === 0 ? "—" : `${r[f]}%`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-[#7F7F7F]">
          grey = not supplied · green ≥90% · amber 50–89% · red &lt;50%
        </p>
      </SectionCard>
    </div>
  );
}
